import type {
    TravelFormDestinationResult,
    TravelFormDestinationResultSetType,
} from '@/features/travel-form/travel-form-data-v1/travel-form-data-v1-type';

import React from 'react';

import Divider from '@/core/components/divider/divider';
import useLoadingIndicator from '@/core/components/loading/use-loading-indicator';
import { LoadingSpinnerIcon } from '@/core/features';
import IconImage from '@/core/features/image-v2/icon-image';
import color from '@/core/features/styles/color';
import TravelFormDestinationResultSetDesktop from '@/features/travel-form/travel-form-overlay/travel-form-destination-overlay/travel-form-destination-overlay-desktop/travel-form-destination-result-list-desktop/travel-form-destination-result-set-desktop';
import { TravelFormSearchShimmerBoxDesktop } from '@/features/travel-form/travel-form-overlay/travel-form-shimmer-boxes-desktop';

type TravelFormDestinationProps = {
    isLoading: boolean;
    noResult: {
        iconUrl: string;
        text: string;
    } | null;
    onDestinationResultClick: (
        event: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>,
        destination: TravelFormDestinationResult,
        enableNextInputOverlay?: boolean,
    ) => void;
    resultSet: TravelFormDestinationResultSetType[];
    searchQuery: string;
    selectedDestinationsByName: string[];
};

export default function TravelFormDestinationDesktop({
    isLoading,
    noResult,
    onDestinationResultClick,
    resultSet,
    searchQuery,
    selectedDestinationsByName,
}: TravelFormDestinationProps) {
    const showShimmerBoxLoading = resultSet.length === 0;
    const showLoadingIndicator = useLoadingIndicator(isLoading && !showShimmerBoxLoading);
    const [isHoveringAnyDestinationResult, setIsHoveringAnyDestinationResult] = React.useState(false);

    if (showShimmerBoxLoading) {
        return <TravelFormSearchShimmerBoxDesktop />;
    }

    return (
        <div
            className={`travelFormDestinationOverlayContentDesktop w-full ${isLoading || showLoadingIndicator ? 'overlayContent--isLoading' : ''}`}
        >
            {noResult && (
                <>
                    <div className={'flex justify-between items-center pt-20 pl-20 pr-20 bg-white'}>
                        <div className={'text-16 font-bold line-clamp-2'}>{noResult.text}</div>
                        <div
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ marginLeft: 30, opacity: 0.5 }}
                        >
                            <IconImage
                                alt={'no-result-icon'}
                                height={30}
                                url={noResult.iconUrl}
                                width={30}
                            />
                        </div>
                    </div>
                    <Divider
                        color={'gallery'}
                        marginBottom={20}
                        marginLeft={20}
                        marginRight={20}
                        marginTop={20}
                    />
                </>
            )}
            {resultSet.map((result, index) => (
                <React.Fragment key={`${result.title ?? 'destination-fallback-title'}-${index}`}>
                    <TravelFormDestinationResultSetDesktop
                        isFirstResultSet={index === 0}
                        isHoveringAnyDestinationResult={isHoveringAnyDestinationResult}
                        onResultClick={onDestinationResultClick}
                        resultSet={result}
                        searchQuery={searchQuery}
                        selectedDestinationsByName={selectedDestinationsByName}
                        setIsHoveringAnyDestinationResult={setIsHoveringAnyDestinationResult}
                    />
                    {index !== resultSet.length - 1 && (
                        <Divider
                            color={'gallery'}
                            marginBottom={20}
                            marginLeft={20}
                            marginRight={20}
                        />
                    )}
                </React.Fragment>
            ))}
            {showLoadingIndicator && (
                <div
                    className={'absolute'}
                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                    style={{ left: 'calc(50% - 20px)', top: 'calc(50% - 20px)' }}
                >
                    <LoadingSpinnerIcon
                        color={color('doveGray')}
                        height={41}
                        width={41}
                    />
                </div>
            )}
            <style jsx>{`
                .travelFormDestinationOverlayContentDesktop:first-child,
                .travelFormDestinationOverlayContentDesktop:last-child {
                    padding: 0;
                }
                .overlayContent--isLoading::after {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    content: '';
                    background-color: var(--color-white-05);
                }
            `}</style>
        </div>
    );
}
